import { useMemo, useRef, useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  Input,
  PageHeader,
  RadioCard,
  RadioCardGroup,
  Stack,
  TagInput,
  Textarea,
  Toggle,
  useDelayedLoading,
} from '@leanin/ui';
import type { CircleFormat } from '@leanin/shared/circle-format';
import { CIRCLE_MAX_MEMBERS_CAP } from '@leanin/shared/circle-limits';
import { SaveBar } from '../../form/SaveBar';
import type { CircleEditorFields, GroupDetail } from '../../../lib/views/groups';
import { useCircle } from '../../../lib/queries/groups';
import { useUpdateCircle, type UpdateCircleInput } from '../../../lib/mutations/groups';
import { circleSaveErrorMessage, isIndustrySpecificWithoutIndustry } from '../circle-save-error';
import { CircleIndustryFields, circleIndustryOf, type CircleIndustry } from '../CircleIndustryFields';
import { LanguageSelect } from '../LanguageSelect';
import {
  CircleLeaderFactsFields,
  activeTopicIds,
  circleLeaderFactsOf,
  parseCircleLeaderFacts,
  type CircleLeaderFacts,
  type CircleLeaderFactsErrors,
  type ParsedCircleLeaderFacts,
  useDiscussionTopics,
} from '../CircleLeaderFactsFields';
import { useErrorMessage } from '../../../lib/error-message';
import { parseWholeNumberInput } from '../../../lib/whole-number-input';
import { useFocusFirstError } from '../../form/use-focus-first-error';
import { useCapabilities } from '../../../lib/use-session';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '../../group/admin/AdminImagePanel';
import { AdminFormPageSkeleton } from '../../group/admin/AdminFormPageSkeleton';
import { useLandOnField } from '../../group/admin/use-land-on-field';
import { PlacePicker, type PlaceSelection } from '../../geo';

// Per-circle tags. Same input pattern as networks (free-form string[]),
// so circle leaders can describe their circle in their own words. The
// curated discussion topics (at most CIRCLE_MAX_TOPICS) sit beside the
// meeting cadence and target size (CircleLeaderFactsFields).

type Affiliated = 'yes' | 'no' | '';
type OpenToNew = 'yes' | 'no';

function initialAffiliated(circle: GroupDetail): Affiliated {
  if (circle.affiliatedWithCompany === true) return 'yes';
  if (circle.affiliatedWithCompany === false) return 'no';
  return '';
}

// "Open to new members?" round-trip:
//   - 'open' or 'request' → 'yes' (anyone can ask to join)
//   - 'invite_only'       → 'no'  (leaders pick the members)
// 'open' is deprecated (Start-a-Circle never picks it); selecting "Yes"
// from the admin form writes back 'request' to match the create flow.
function initialOpenToNew(circle: GroupDetail): OpenToNew {
  return circle.joinPolicy === 'invite_only' ? 'no' : 'yes';
}

// The Circle as the server holds it, with the leader-entered facts only its
// editors are sent (GET /circles/{id} `editorFields`).
type OverviewBase = {
  readonly circle: GroupDetail;
  readonly editorFields: CircleEditorFields;
};

// The form's values for a Circle as the server holds it: when the form opens,
// and again from the saved Circle after each save, which also brings in what
// a co-leader changed meanwhile.
function formValuesOf({ circle, editorFields }: OverviewBase) {
  const place = circle.place ?? null;
  return {
    name: circle.name,
    description: circle.description ?? '',
    format: (circle.format ?? '') as CircleFormat | '',
    place:
      place === null
        ? null
        : {
            placeId: place.id,
            placeText: place.text,
            placeLat: place.lat,
            placeLng: place.lng,
            placeCountryCode: place.countryCode,
          },
    affiliated: initialAffiliated(circle),
    company: circle.affiliatedCompany ?? '',
    openToNew: initialOpenToNew(circle),
    tags: circle.tags ?? [],
    industry: circleIndustryOf(circle),
    language: circle.language,
    maxMembers: circle.maxMembers === null ? '' : String(circle.maxMembers),
    acceptingMembers: circle.acceptingMembers,
    leaderFacts: circleLeaderFactsOf({ ...editorFields, topics: circle.topics }),
  };
}

// The fields a refused save marks in error. The leader-facts fields carry
// their own (CircleLeaderFactsErrors).
type OverviewFieldError = 'name' | 'company' | 'maxMembers';

type OverviewDraft = {
  readonly name: string;
  readonly description: string | null;
  readonly format: CircleFormat | '';
  readonly place: PlaceSelection | null;
  readonly tags: readonly string[];
  readonly openToNew: OpenToNew;
  readonly affiliated: Affiliated;
  readonly company: string;
  readonly industry: CircleIndustry;
  readonly language: string | null;
  readonly maxMembers: number | null;
  readonly acceptingMembers: boolean;
  readonly leaderFacts: ParsedCircleLeaderFacts;
  readonly activeTopicIds: ReadonlySet<string>;
};

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

type OverviewPatch = Omit<UpdateCircleInput, 'circleId' | 'expectedVersion'>;

// Only the fields the leader changed. Re-sending an untouched value would
// re-validate it: a language tag stored before the server's tag grammar
// existed would then fail every save, whatever else the leader edited.
function changedFields({ circle, editorFields }: OverviewBase, d: OverviewDraft): OverviewPatch {
  const affiliationChanged =
    d.affiliated !== initialAffiliated(circle) ||
    (d.affiliated === 'yes' && d.company !== (circle.affiliatedCompany ?? ''));
  // Translate the tri-state answer into the column pair the API validates
  // against `circles_affiliation_shape`. '' (legacy) → don't send → server
  // leaves the columns alone.
  const affiliation =
    !affiliationChanged || d.affiliated === ''
      ? {}
      : d.affiliated === 'yes'
        ? { affiliatedWithCompany: true, affiliatedCompany: d.company }
        : { affiliatedWithCompany: false, affiliatedCompany: null };
  return {
    ...(d.name !== circle.name ? { name: d.name } : {}),
    ...(d.description !== circle.description ? { description: d.description } : {}),
    ...(d.format !== '' && d.format !== circle.format ? { format: d.format } : {}),
    ...((d.place?.placeId ?? null) !== (circle.place?.id ?? null) ? { place: d.place } : {}),
    ...(JSON.stringify(d.tags) !== JSON.stringify(circle.tags ?? []) ? { tags: [...d.tags] } : {}),
    // Match the Start-a-Circle mapping: Yes → request, No → invite_only.
    ...(d.openToNew !== initialOpenToNew(circle)
      ? { joinPolicy: d.openToNew === 'yes' ? 'request' : 'invite_only' }
      : {}),
    ...affiliation,
    ...(d.industry.industryCode !== circle.industryCode ? { industryCode: d.industry.industryCode } : {}),
    ...(d.industry.industrySpecific !== circle.industrySpecific
      ? { industrySpecific: d.industry.industrySpecific }
      : {}),
    ...(d.language !== circle.language ? { language: d.language } : {}),
    ...(d.maxMembers !== circle.maxMembers ? { maxMembers: d.maxMembers } : {}),
    ...(d.acceptingMembers !== circle.acceptingMembers ? { acceptingMembers: d.acceptingMembers } : {}),
    ...(d.leaderFacts.meetingCadence !== editorFields.meetingCadence ? { meetingCadence: d.leaderFacts.meetingCadence } : {}),
    ...(d.leaderFacts.targetSize !== editorFields.targetSize ? { targetSize: d.leaderFacts.targetSize } : {}),
    ...topicsPatch(circle, d),
  };
}

// Only active topics are the leader's to edit; the server keeps the rest.
function topicsPatch(circle: GroupDetail, d: OverviewDraft): Pick<OverviewPatch, 'topicIds'> {
  const stored = activeTopicIds(circle.topics.map((t) => t.id), d.activeTopicIds);
  return sameIds(d.leaderFacts.topicIds, stored) ? {} : { topicIds: d.leaderFacts.topicIds };
}

// The fields a `?field=` deep link can land on: the Circle profile facts
// behind the directory's completeness score, in the server's PROFILE_FIELDS
// order.
export const CIRCLE_OVERVIEW_FIELDS = [
  'description',
  'cover',
  'location',
  'format',
  'language',
  'industry',
  'cadence',
] as const;
export type CircleOverviewField = (typeof CIRCLE_OVERVIEW_FIELDS)[number];

export function circleOverviewField(raw: string | undefined): CircleOverviewField | undefined {
  return CIRCLE_OVERVIEW_FIELDS.find((f) => f === raw);
}

type Props = {
  readonly circleId: string;
  /** Scroll to and focus this field once the form is up. */
  readonly landOnField?: CircleOverviewField | undefined;
};

export function CircleOverviewSection({ circleId, landOnField }: Props) {
  const detailQuery = useCircle(circleId);

  const detail = detailQuery.data;
  const showSkeleton = useDelayedLoading(
    detail === undefined || detail.access === 'visitor',
  );
  if (showSkeleton) {
    return <AdminFormPageSkeleton withAction cards={2} rowsPerCard={4} />;
  }
  // The form edits what only an editor of the Circle is sent: a member who
  // can't edit it, or the leader of an archived Circle, gets no form.
  if (detail === undefined || detail.access === 'visitor' || detail.editorFields === null) return null;

  return <CircleOverviewForm circle={detail.circle} editorFields={detail.editorFields} landOnField={landOnField} />;
}

type FormProps = {
  readonly circle: GroupDetail;
  readonly editorFields: CircleEditorFields;
  readonly landOnField: CircleOverviewField | undefined;
};

function CircleOverviewForm({ circle, editorFields, landOnField }: FormProps) {
  const tc = useTranslations('Circles');
  const describeError = useErrorMessage();
  const updateMutation = useUpdateCircle();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const coverRef = useRef<HTMLButtonElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const formatRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLSelectElement>(null);
  const industryRef = useRef<HTMLSelectElement>(null);
  const cadenceRef = useRef<HTMLSelectElement>(null);
  const landTargets = useMemo(
    () => ({
      description: descriptionRef,
      cover: coverRef,
      location: locationRef,
      format: formatRef,
      language: languageRef,
      industry: industryRef,
      cadence: cadenceRef,
    }),
    [],
  );
  useLandOnField(landOnField, landTargets);
  const capabilities = useCapabilities();
  // The accepting helper says the Circle is shown lower in the directory,
  // which holds only while this leader is served ranking v2: the v1 ranker
  // has no listing tiers.
  const listingTiers = capabilities?.circleListingTiers === true;
  const profileFields = capabilities?.circleProfileFields === true;

  // What the form diffs against: the Circle as it was when the form opened or
  // last saved, not the live query. A refetch that brings in a co-leader's
  // edit must not make a field this leader never touched look changed, or the
  // save would write the old value back over theirs.
  const [base, setBase] = useState<OverviewBase>({ circle, editorFields });
  const initial = formValuesOf(base);
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [format, setFormat] = useState<CircleFormat | ''>(initial.format);
  // Structured place selection — replaces the legacy free-text `location`
  // column. Hydrated from the server's `place` view if set; the picker
  // trusts the parent's text and does not re-fetch.
  const [place, setPlace] = useState<PlaceSelection | null>(initial.place);
  // Tri-state affiliation pair. '' = legacy/unset; the radio renders empty.
  const [affiliated, setAffiliated] = useState<Affiliated>(initial.affiliated);
  const [company, setCompany] = useState(initial.company);
  const [openToNew, setOpenToNew] = useState<OpenToNew>(initial.openToNew);
  const [tags, setTags] = useState<string[]>(initial.tags);
  const [industry, setIndustry] = useState<CircleIndustry>(initial.industry);
  const [language, setLanguage] = useState<string | null>(initial.language);
  const [maxMembers, setMaxMembers] = useState(initial.maxMembers);
  const [acceptingMembers, setAcceptingMembers] = useState(initial.acceptingMembers);
  const [leaderFacts, setLeaderFacts] = useState<CircleLeaderFacts>(initial.leaderFacts);
  const [leaderFactsErrors, setLeaderFactsErrors] = useState<CircleLeaderFactsErrors>({});
  const discussionTopics = useDiscussionTopics({ enabled: profileFields });
  const [error, setError] = useState<string | null>(null);
  const [specificError, setSpecificError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ReadonlySet<OverviewFieldError>>(new Set());
  const formRef = useRef<HTMLElement | null>(null);
  const focusFirstError = useFocusFirstError(formRef);

  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committing, setCommitting] = useState(false);
  const isPending = updateMutation.isPending || committing;

  const textDirty =
    name.trim() !== initial.name.trim() ||
    description.trim() !== initial.description.trim() ||
    format !== initial.format ||
    (place?.placeId ?? null) !== (initial.place?.placeId ?? null) ||
    affiliated !== initial.affiliated ||
    (affiliated === 'yes' && company.trim() !== initial.company) ||
    openToNew !== initial.openToNew ||
    JSON.stringify(tags) !== JSON.stringify(initial.tags) ||
    industry.industryCode !== initial.industry.industryCode ||
    industry.industrySpecific !== initial.industry.industrySpecific ||
    language !== initial.language ||
    maxMembers.trim() !== initial.maxMembers ||
    acceptingMembers !== initial.acceptingMembers ||
    leaderFacts.meetingCadence !== initial.leaderFacts.meetingCadence ||
    leaderFacts.targetSize.trim() !== initial.leaderFacts.targetSize ||
    !sameIds(
      activeTopicIds(leaderFacts.topicIds, discussionTopics.activeIds),
      activeTopicIds(initial.leaderFacts.topicIds, discussionTopics.activeIds),
    );
  const dirty = textDirty || imagesPending;

  const clearFieldError = (field: OverviewFieldError) => {
    setFieldErrors((prev) => new Set([...prev].filter((f) => f !== field)));
  };

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    setSpecificError(null);
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    // Hidden profile fields are never edited, so they are never checked
    // either: an error on a field the leader can't see would block the save
    // with no message.
    const cap = profileFields
      ? parseWholeNumberInput(maxMembers, { min: 1, max: CIRCLE_MAX_MEMBERS_CAP })
      : { ok: true as const, value: base.circle.maxMembers };
    const facts = profileFields
      ? parseCircleLeaderFacts(leaderFacts, discussionTopics.activeIds)
      : {
          ok: true as const,
          value: {
            meetingCadence: base.editorFields.meetingCadence,
            targetSize: base.editorFields.targetSize,
            topicIds: activeTopicIds(base.circle.topics.map((t) => t.id), discussionTopics.activeIds),
          },
        };
    const nextFieldErrors = new Set<OverviewFieldError>([
      ...(trimmedName.length === 0 ? (['name'] as const) : []),
      ...(affiliated === 'yes' && company.trim().length === 0 ? (['company'] as const) : []),
      ...(cap.ok ? [] : (['maxMembers'] as const)),
    ]);
    setFieldErrors(nextFieldErrors);
    setLeaderFactsErrors(facts.ok ? {} : facts.errors);
    // Any field in error holds back the whole save, so nothing the leader sees
    // as entered is silently left out of it.
    if (!cap.ok || !facts.ok || nextFieldErrors.size > 0) {
      focusFirstError();
      return;
    }
    if (imagesPending) {
      setCommitting(true);
      try {
        await imagesRef.current?.commit();
      } catch (err) {
        setError(describeError(err, tc('edit.errorImageUpload')));
        setCommitting(false);
        return;
      }
      setCommitting(false);
    }
    if (!textDirty) return;
    const patch = changedFields(base, {
      name: trimmedName,
      description: trimmedDesc.length > 0 ? trimmedDesc : null,
      format,
      place,
      tags,
      openToNew,
      affiliated,
      company: company.trim(),
      industry,
      language,
      maxMembers: cap.value,
      acceptingMembers,
      leaderFacts: facts.value,
      activeTopicIds: discussionTopics.activeIds,
    });
    // An edit that normalizes back to the stored values ("05" for 5, a
    // trailing space) changes nothing; a PATCH carrying only the version
    // would be a write with nothing in it.
    if (Object.keys(patch).length === 0) return;
    updateMutation.mutate(
      {
        circleId: circle.id,
        ...patch,
        // The version the cache last saw. Only fields this leader changed are
        // in the patch, so a co-leader's edit to another field is kept either
        // way; a co-leader's edit to the same field is last-writer-wins.
        expectedVersion: circle.version,
      },
      {
        onSuccess: ({ data: saved, editorFields: savedEditorFields }) => {
          const next: OverviewBase = { circle: saved, editorFields: savedEditorFields };
          const v = formValuesOf(next);
          setBase(next);
          setName(v.name);
          setDescription(v.description);
          setFormat(v.format);
          setPlace(v.place);
          setAffiliated(v.affiliated);
          setCompany(v.company);
          setOpenToNew(v.openToNew);
          setTags(v.tags);
          setIndustry(v.industry);
          setLanguage(v.language);
          setMaxMembers(v.maxMembers);
          setAcceptingMembers(v.acceptingMembers);
          setLeaderFacts(v.leaderFacts);
        },
        onError: (err) => {
          if (isIndustrySpecificWithoutIndustry(err)) {
            setSpecificError(tc('industryFields.errorRequiresIndustry'));
            return;
          }
          setError(circleSaveErrorMessage(err, describeError, tc('edit.errorUpdate'), tc('errors.linkNotAllowed')));
        },
      },
    );
  };

  return (
    <Stack gap="lg" ref={formRef}>
      <PageHeader title={tc('edit.title')} />

      <Card pad="flush">
        <AdminImagePanel
          ref={imagesRef}
          groupKind="circle"
          groupId={circle.id}
          groupName={name}
          currentCoverUrl={circle.imageUrl ?? null}
          currentCoverOriginalUrl={circle.imageOriginalUrl}
          currentCoverCrop={circle.imageCrop}
          coverEyebrow={tc('edit.coverEyebrow')}
          coverButtonRef={coverRef}
          onPendingChange={setImagesPending}
        />

        <Stack p="xl" gap="lg">
          {/* "The basics" mirrors Step 01 of the Start-a-Circle form so the
              create and edit flows feel like the same shape. */}
          <Eyebrow>{tc('edit.basics')}</Eyebrow>
          <Field
            label={tc('edit.nameLabel')}
            required
            error={fieldErrors.has('name') ? tc('edit.errorNameRequired') : null}
          >
            <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError('name');
              }}
              maxLength={120}
            />
          </Field>

          <Field label={tc('edit.descriptionLabel')}>
            <Textarea
              ref={descriptionRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={2000}
            />
          </Field>

          <Field
            label={tc('edit.locationLabel')}
            required
            description={tc('edit.locationDescription')}
          >
            <PlacePicker
              ref={locationRef}
              mode="city"
              value={place}
              onChange={setPlace}
              placeholder={tc('edit.locationPlaceholder')}
              disabled={isPending}
            />
          </Field>

          <Field label={tc('edit.affiliatedQuestion')} required>
            <RadioCardGroup
              value={affiliated}
              onValueChange={(v) => setAffiliated(v as Affiliated)}
              columns={2}
              aria-label={tc('edit.affiliatedQuestion')}
            >
              <RadioCard value="yes" title={tc('edit.yes')} />
              <RadioCard value="no" title={tc('edit.no')} />
            </RadioCardGroup>
          </Field>
          {affiliated === 'yes' ? (
            <Field
              label={tc('edit.whichCompany')}
              required
              error={fieldErrors.has('company') ? tc('edit.errorCompanyRequired') : null}
            >
              <Input
                placeholder={tc('edit.companyPlaceholder')}
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  clearFieldError('company');
                }}
                disabled={isPending}
              />
            </Field>
          ) : null}
        </Stack>
      </Card>

      <Card pad="default">
        <Stack gap="lg">
          {/* "How you'll meet" mirrors Step 02 of the Start-a-Circle form:
              Open-to-new-members (joinPolicy) + Format as a 3-up
              RadioCardGroup (Virtual / In-person / Hybrid). Tags ride along
              here per leader preference. */}
          <Eyebrow>{tc('edit.howYoullMeet')}</Eyebrow>
          <Field
            label={tc('edit.openQuestion')}
            required
            description={tc('edit.openDescription')}
          >
            <RadioCardGroup
              value={openToNew}
              onValueChange={(v) => setOpenToNew(v as OpenToNew)}
              columns={2}
              aria-label={tc('edit.openQuestion')}
            >
              <RadioCard value="yes" title={tc('edit.yes')} />
              <RadioCard value="no" title={tc('edit.no')} />
            </RadioCardGroup>
          </Field>
          <Field label={tc('edit.meetFormatLabel')} required>
            <RadioCardGroup
              ref={formatRef}
              value={format}
              onValueChange={(v) => setFormat(v as CircleFormat)}
              columns={3}
              aria-label={tc('edit.meetingFormatAria')}
            >
              <RadioCard value="virtual" icon={I.globe} title={tc('edit.formatVirtual')} />
              <RadioCard value="in_person" icon={I.pin} title={tc('edit.formatInPerson')} />
              <RadioCard value="hybrid" icon={I.users} title={tc('edit.formatHybrid')} />
            </RadioCardGroup>
          </Field>

          {profileFields ? (
            <Stack gap="md">
              <Heading level={5}>{tc('edit.whoItsFor')}</Heading>
              <CircleIndustryFields
                value={industry}
                onChange={(next) => {
                  setIndustry(next);
                  setSpecificError(null);
                }}
                specificError={specificError}
                disabled={isPending}
                industryRef={industryRef}
              />
              <Field label={tc('languageSelect.label')}>
                <LanguageSelect ref={languageRef} value={language} onChange={setLanguage} allowEmpty disabled={isPending} />
              </Field>
              <Field
                label={tc('edit.maxMembersLabel')}
                description={tc('edit.maxMembersDescription')}
                error={
                  fieldErrors.has('maxMembers') ? tc('edit.errorMaxMembers', { max: CIRCLE_MAX_MEMBERS_CAP }) : null
                }
              >
                <Input
                  type="text"
                  inputMode="numeric"
                  value={maxMembers}
                  onChange={(e) => {
                    setMaxMembers(e.target.value);
                    clearFieldError('maxMembers');
                  }}
                  disabled={isPending}
                />
              </Field>
              <Field
                layout="inline"
                label={tc('edit.acceptingLabel')}
                {...(listingTiers ? { description: tc('edit.acceptingDescription') } : {})}
              >
                <Toggle
                  on={acceptingMembers}
                  onChange={() => setAcceptingMembers(!acceptingMembers)}
                  disabled={isPending}
                  label={tc('edit.acceptingLabel')}
                />
              </Field>
              <CircleLeaderFactsFields
                value={leaderFacts}
                onChange={(next) => {
                  setLeaderFacts(next);
                  setLeaderFactsErrors({});
                }}
                errors={leaderFactsErrors}
                topics={discussionTopics}
                disabled={isPending}
                cadenceRef={cadenceRef}
              />
            </Stack>
          ) : null}

          <Field>
            <Field.Label>{tc('edit.tagsLabel')}</Field.Label>
            <TagInput
              value={tags}
              onChange={setTags}
              placeholder={tc('edit.tagPlaceholder')}
              addLabel={tc('edit.addTag')}
              removeLabel={(tag) => tc('edit.removeTagAria', { tag })}
              disabled={isPending}
            />
            <Field.Description>{tc('edit.tagsDescription')}</Field.Description>
          </Field>
        </Stack>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={isPending}
        error={error}
        onSave={handleSave}
      />
    </Stack>
  );
}
